import { ROUTES } from "@/config/routes";
import CommonLayout from "@/containers/app/CommonLayout";
import { Alert, Box, Button, Switch } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useMerchant } from "@/hooks/useMerchant";
import { track } from "@/helpers/posthog";
import { useProtectMerchant } from "../useProtectMerchant";
import { highestIntegralCoverage } from "./integralCoverage";
import { toBenchInput } from "./bench";
import ExitDialog from "./components/ExitDialog";
import WizardStepper from "@/components/WizardStepper";
import BenchStep from "./steps/BenchStep";
import ClosingStep from "./steps/ClosingStep";
import SavingStep from "./steps/SavingStep";
import SelectionStep from "./steps/SelectionStep";
import SummaryStep from "./steps/SummaryStep";
import { buildSubmitPayload } from "./submitPayload";
import type { QuoteRow } from "./types";
import { useInductionPreApproved } from "./useInductionPreApproved";
import { useInductionSubmit } from "./useInductionSubmit";
import { useInductionSummary } from "./useInductionSummary";
import { STEPS, useInductionWizard } from "./useInductionWizard";

const InductionContainer = (): JSX.Element | null => {
  const navigate = useNavigate();
  const { protectMerchant, isLoading } = useProtectMerchant();
  const {
    summary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
  } = useInductionSummary();
  const { state, actions } = useInductionWizard();
  const [exitOpen, setExitOpen] = useState(false);
  const { merchant } = useMerchant();
  const portfolio = merchant?.contractCount || undefined;
  const bench = useMemo(
    () => toBenchInput(state.bench, portfolio),
    [state.bench, portfolio]
  );
  const integralCoverage = useMemo(
    () =>
      highestIntegralCoverage(state.bench.map((row) => row.integralCoverage)),
    [state.bench]
  );
  const { screenings } = useInductionPreApproved();

  // Lives here, not in the Resumen step, so the idempotency key survives going
  // back and forth after an unknown outcome.
  const { submit, isSubmitting, isUnknown } = useInductionSubmit();

  // Decided once, from the first answer. Later refetches (for instance after
  // the batch is submitted) must not send the merchant away.
  const [canStay, setCanStay] = useState<boolean>();
  useEffect(() => {
    if (canStay !== undefined || isSummaryLoading) return;
    setCanStay(!isSummaryError && (summary?.availableCount ?? 0) > 0);
  }, [canStay, isSummaryLoading, isSummaryError, summary]);

  const handleSubmit = async (
    quoted: Pick<QuoteRow, "pct" | "rateWithVat" | "monthlyCost">
  ) => {
    if (!screenings || !state.accuracy) return;

    const outcome = await submit(
      buildSubmitPayload({
        bench,
        screenings,
        selected: state.selected,
        integralCoverage,
        quoted,
      })
    );
    if (outcome?.status === "ok") actions.submitted(outcome.result);
    if (outcome?.status === "rejected")
      actions.rejected(outcome.failure, outcome.orderedIds);
  };

  // Progress is not saved: warn before a reload or a closed tab loses it.
  // Once the batch exists there is nothing left to lose.
  const isClosed = state.result !== null;

  useEffect(() => {
    if (canStay) track("induction_started");
  }, [canStay]);
  useEffect(() => {
    if (canStay && !isClosed)
      track("induction_step_viewed", { step: state.step });
  }, [canStay, isClosed, state.step]);
  useEffect(() => {
    if (isClosed) track("induction_submitted");
  }, [isClosed]);

  useEffect(() => {
    if (!canStay || isClosed) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [canStay, isClosed]);

  if (isLoading) return null;
  if (!protectMerchant) return <Navigate to={ROUTES.DASHBOARD.HOME} replace />;
  if (canStay === undefined) return null;
  if (!canStay) return <Navigate to={ROUTES.DASHBOARD.PROTECT} replace />;

  return (
    <>
      <CommonLayout
        title="Inducción a Fianza Palomma"
        subtitle="Confidencial. Solo sirve para mostrarte tus pre-aprobados con la mejor tarifa."
        buttons={
          isClosed ? undefined : (
            <Button variant="outline" onClick={() => setExitOpen(true)}>
              Salir sin guardar
            </Button>
          )
        }
        scrollResetKey={state.step}
        table={
          <Box display="flex" flexDirection="column" gap="1.5rem">
            <WizardStepper
              steps={STEPS}
              current={state.step}
              label="Pasos de la inducción"
            />
            {state.notice === "screeningsUnavailable" && (
              <Alert.Root status="error" colorPalette="red">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Description>
                    Algunos de estos contratos ya fueron inducidos. Revisa la
                    pestaña Contratos o vuelve a elegir.
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}
            {state.notice === "listChanged" && (
              <Alert.Root status="warning" colorPalette="orange">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Description>
                    Tu lista de pre-aprobados cambió mientras revisabas. Revisa
                    tu selección y vuelve a continuar.
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}
            {state.notice === "quoteChanged" && (
              <Alert.Root status="warning" colorPalette="orange">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Description>
                    Los números cambiaron mientras revisabas. Revisa el resumen
                    y vuelve a confirmar.
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}
            {(state.step > 0 || isClosed) && (
              <Switch.Root
                alignSelf="flex-end"
                checked={state.withVat}
                onCheckedChange={({ checked }) => actions.setWithVat(checked)}
              >
                <Switch.HiddenInput />
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
                <Switch.Label>
                  Ver tasas {state.withVat ? "con IVA" : "sin IVA"}
                </Switch.Label>
              </Switch.Root>
            )}
            {state.step === 0 && (
              <BenchStep
                rows={state.bench}
                portfolio={portfolio}
                onAdd={actions.addBenchRow}
                onRemove={actions.removeBenchRow}
                onEdit={actions.editBenchRow}
                onNext={() => {
                  const vat = state.bench.map((row) => row.includesVat);
                  if (vat.every(Boolean)) actions.setWithVat(true);
                  else if (!vat.some(Boolean)) actions.setWithVat(false);
                  actions.goTo(1);
                }}
              />
            )}
            {state.step === 1 && (
              <SavingStep
                bench={bench}
                withVat={state.withVat}
                onBack={() => actions.goTo(0)}
                onNext={() => actions.goTo(2)}
              />
            )}
            {state.step === 2 && (
              <SelectionStep
                bench={bench}
                withVat={state.withVat}
                selected={state.selected}
                pct={state.pct}
                search={state.search}
                provider={state.provider}
                onPct={actions.setPct}
                onSearch={actions.setSearch}
                onProvider={actions.setProvider}
                onBack={() => actions.goTo(1)}
                onContinue={() => actions.goTo(3)}
              />
            )}
            {state.step === 3 && (
              <SummaryStep
                bench={bench}
                withVat={state.withVat}
                selected={state.selected}
                integralCoverage={integralCoverage}
                accuracy={state.accuracy}
                onAccuracy={actions.setAccuracy}
                isSubmitting={isSubmitting}
                isUnknown={isUnknown}
                onBack={() => actions.goTo(2)}
                onSubmit={handleSubmit}
              />
            )}
            {state.result && (
              <ClosingStep
                batch={state.result}
                withVat={state.withVat}
                onFinish={() => navigate(ROUTES.DASHBOARD.PROTECT)}
              />
            )}
          </Box>
        }
      />
      <ExitDialog
        open={exitOpen}
        onClose={() => setExitOpen(false)}
        onConfirm={() => {
          track("induction_exit_confirmed", { step: state.step });
          navigate(ROUTES.DASHBOARD.PROTECT);
        }}
      />
    </>
  );
};

export default InductionContainer;
